import {
  faCheck,
  faEnvelope,
  faMobileAlt,
  faPen,
  faPhoneAlt,
  faTag,
  faTimes,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import ContactCustomerEditModal from 'components/modals/ContactCustomerEditModal';
import ContactCustomerRegistrationModal from 'components/modals/CustomerContactRegistrationModal';
import CustomerDomainEditModal from 'components/modals/CustomerDomainEditModal';
import CustomerDomainRegistrationModal from 'components/modals/CustomerDomainRegistrationModal';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import CustomerContactsService from 'services/CustomerContacts.service';

const CustomerContact = ({ data: customerData }) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  // State management
  const [customer, setCustomer] = useState({});
  const [contacts, setContacts] = useState({ content: [] });
  const [domains, setDomains] = useState({ content: [] });
  const [selectedContact, setSelectedContact] = useState({});
  const [selectedDomain, setSelectedDomain] = useState({});

  // Modal visibility states
  const [showDialog, setShowDialog] = useState(false);
  const [showModalDomainRegistration, setShowModalDomainRegistration] =
    useState(false);
  const [showModalRegistration, setShowModalRegistration] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalEditDomain, setShowModalEditDomain] = useState(false);

  // Pagination filters
  const [filterContacts, setFilterContacts] = useState({ page: 0, size: 20 });
  const [filterDomains, setFilterDomains] = useState({ page: 0, size: 20 });

  // Initialize customer data
  useEffect(() => {
    setCustomer(customerData);
  }, [customerData]);

  // Fetch contacts when customer or filters change
  useEffect(() => {
    if (Object.keys(customer).length !== 0) {
      fetchContacts();
    }
  }, [customer, filterContacts]);

  // Fetch domains when customer or filters change
  useEffect(() => {
    if (Object.keys(customer).length !== 0) {
      fetchDomains();
    }
  }, [customer, filterDomains]);

  // Data fetching functions
  const fetchContacts = useCallback(async () => {
    const queryParams = new URLSearchParams(filterContacts).toString();
    const response = await CustomerContactsService.list(
      customer.id,
      queryParams
    );

    if (response.userMessage) {
      // Assumindo que response.userMessage indica erro
      showErrorNotification(response.userMessage || 'Failed to fetch contacts');
    } else {
      setContacts(response);
    }
  }, [customer.id, filterContacts, showErrorNotification]);

  const fetchDomains = useCallback(async () => {
    const queryParams = new URLSearchParams(filterDomains).toString();
    const response = await api.allowedDomains.list(customer.id, queryParams);

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to fetch domains');
    } else {
      setDomains(response);
    }
  }, [api.allowedDomains, customer.id, filterDomains, showErrorNotification]);

  // CRUD operations for contacts
  const addContact = async body => {
    const response = await CustomerContactsService.add(body);

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to add contact');
    } else {
      showSuccessNotification('Contato adicionado com sucesso');
      fetchContacts();
      handleCloseModalRegistration();
    }
  };

  const editContact = async body => {
    const response = await CustomerContactsService.edit(
      selectedContact.id,
      body
    );

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to edit contact');
    } else {
      showSuccessNotification('Contato editado com sucesso');
      fetchContacts();
      handleCloseModalEdit();
    }
  };

  // CRUD operations for domains
  const addDomain = async body => {
    const response = await api.allowedDomains.add(customer.id, body);

    if (response.userMessage) {
      console.log(response);
      showErrorNotification(response.userMessage || 'Failed to add domain');
    } else {
      showSuccessNotification('Domínio liberado com sucesso');
      fetchDomains();
      handleCloseModalDomainRegistration();
    }
  };

  const editDomain = async body => {
    const response = await api.allowedDomains.edit(
      customer.id,
      selectedDomain.id,
      body
    );

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to edit domain');
    } else {
      showSuccessNotification('Domínio editado com sucesso');
      fetchDomains();
      handleCloseModalEditDomain();
    }
  };

  const deleteDomain = async confirm => {
    if (!confirm) {
      handleCloseDialog();
      return;
    }

    const response = await api.allowedDomains.delete(
      customer.id,
      selectedDomain.id
    );

    if (response.userMessage) {
      showErrorNotification(response.userMessage || 'Failed to delete domain');
    } else {
      showSuccessNotification('Domínio excluído com sucesso');
      fetchDomains();
      handleCloseDialog();
    }
  };

  // Modal handlers
  const handleCloseModalRegistration = () => setShowModalRegistration(false);
  const handleCloseModalDomainRegistration = () =>
    setShowModalDomainRegistration(false);
  const handleCloseModalEdit = () => setShowModalEdit(false);
  const handleCloseModalEditDomain = () => setShowModalEditDomain(false);
  const handleCloseDialog = () => setShowDialog(false);

  // Action handlers
  const handleEditContact = item => {
    setSelectedContact(item);
    setShowModalEdit(true);
  };

  const handleEditDomain = item => {
    setSelectedDomain(item);
    setShowModalEditDomain(true);
  };

  const handleDeleteDomain = item => {
    setSelectedDomain(item);
    setShowDialog(true);
  };

  // Table configurations
  const domainColumns = [
    {
      accessorKey: 'domain',
      header: 'Domínio',
      meta: { headerProps: { className: 'text-900' } }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: { headerProps: { className: 'text-900' } },
      cell: ({ row }) => (
        <>
          <IconButton
            className="ms-2"
            iconClassName="fs--2"
            variant="link"
            size="sm"
            icon="pencil-alt"
            onClick={() => handleEditDomain(row.original)}
          />
          <IconButton
            className="ms-2"
            iconClassName="fs--2"
            variant="link"
            size="sm"
            icon="trash"
            onClick={() => handleDeleteDomain(row.original)}
          />
        </>
      )
    }
  ];

  const contactColumns = [
    {
      accessorKey: 'name',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900 w-50' }
      },
      cell: rowData => {
        const { name, tags } = rowData.row.original;
        return (
          <div className="w-100">
            <Flex alignItems="center">
              <FontAwesomeIcon
                icon={faUserCircle}
                className="me-1 text-primary"
                size="lg"
              />
              <span>{name}</span>
            </Flex>
            {tags && tags.length > 0 && (
              <Flex alignItems="center" wrap="wrap" className="mt-2 w-100">
                {tags.map(tag => (
                  <Badge
                    bg="light"
                    text="dark"
                    key={tag.id}
                    className="me-1 mb-1 fs-11 text-center"
                  >
                    <FontAwesomeIcon icon={faTag} className="me-1" size="sm" />
                    {tag.name}
                  </Badge>
                ))}
              </Flex>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'contact',
      header: 'Contato',
      meta: {
        headerProps: { className: 'text-900 w-25' }
      },
      cell: rowData => {
        const { email, landline, phone } = rowData.row.original;
        return (
          <Flex direction="column">
            {email && (
              <div>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{email}</span>
              </div>
            )}
            {phone && (
              <div>
                <FontAwesomeIcon
                  icon={faMobileAlt}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{phone}</span>
              </div>
            )}
            {landline && (
              <div>
                <FontAwesomeIcon
                  icon={faPhoneAlt}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{landline}</span>
              </div>
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'isPortalUser',
      header: 'Portal do Cliente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { isPortalUser } = rowData.row.original;
        return (
          <Flex alignItems="center">
            {isPortalUser ? (
              <FontAwesomeIcon icon={faCheck} className="text-success" />
            ) : (
              <FontAwesomeIcon icon={faTimes} className="text-danger" />
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Ativo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return inactive ? (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        ) : (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <button
            className="btn btn-link btn-sm"
            onClick={() => handleEditContact(item)}
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </button>
        );
      }
    }
  ];

  const contactTable = useAdvanceTable({
    data: contacts.content,
    columns: contactColumns
  });

  return (
    <>
      {/* Modals */}
      <ContactCustomerRegistrationModal
        showModal={showModalRegistration}
        handleClose={handleCloseModalRegistration}
        handleSubmit={addContact}
        customer={customer}
      />
      <ContactCustomerEditModal
        data={selectedContact}
        showModal={showModalEdit}
        handleClose={handleCloseModalEdit}
        handleSubmit={editContact}
        refreshData={fetchContacts}
      />
      <CustomerDomainRegistrationModal
        showModal={showModalDomainRegistration}
        handleClose={handleCloseModalDomainRegistration}
        handleSubmit={addDomain}
      />
      <CustomerDomainEditModal
        data={selectedDomain}
        showModal={showModalEditDomain}
        handleClose={handleCloseModalEditDomain}
        handleSubmit={editDomain}
      />
      <DialogModal
        message="Tem certeza que deseja excluir este domínio?"
        showModal={showDialog}
        handleClose={handleCloseDialog}
        response={deleteDomain}
      />

      <Flex justifyContent="between" alignItems="center">
        <h6 className="fw-semi-bold ls ps-3 mb-3 text-uppercase">Contatos</h6>
        <IconButton
          iconClassName="fs--2 me-1"
          variant="primary"
          size="sm"
          icon="plus"
          onClick={() => setShowModalRegistration(true)}
        >
          Novo
        </IconButton>
      </Flex>
      {contacts.content.length > 0 ? (
        <>
          <AdvanceTableProvider {...contactTable}>
            <AdvanceTable
              headerClassName="text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </AdvanceTableProvider>
          <Pagination
            data={contacts}
            filters={filterContacts}
            setFilters={setFilterContacts}
          />
        </>
      ) : contacts.loading ? (
        <Skeleton height={100} baseColor="#5E6E82" />
      ) : (
        <EmptyList imgSize={350} vhSize={60} />
      )}
    </>
  );
};

export default CustomerContact;
